import { UserContext } from "contexts/UserContext";
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { BanknotesIcon } from "@heroicons/react/24/outline";
import { ArrowUpRightIcon } from "@heroicons/react/24/solid";
import { useHistory } from "react-router";
import LaunchableMeta from "./LaunchableMeta";
import { OrgContext } from "contexts/OrgContext";
import { LaunchableFromList } from "server";
import InputField from "components/UI-lib/InputField";
import { LaunchableContext } from "contexts/LaunchableContext";
import { BillingContext } from "contexts/BillingContext";
import ShimmerButton from "components/UI-lib/Shimmer/ShimmerButton";
import Pagination from "components/Pagination";
import { Tabs, TabsList, TabsTrigger } from "components/Graphs/Tabs";
import { Spinner } from "@kui-react/spinner";
import LaunchableListHeader from "./LaunchableListHeader";
import { PublishLaunchableModal } from "../Publish/PublishLaunchableModal";
import { useQueryClient } from "@tanstack/react-query";

const getLaunchableCreatedTime = (launchable: LaunchableFromList) => {
  const createdTime = launchable.createdAt
    ? Date.parse(launchable.createdAt)
    : Number.NaN;

  return Number.isNaN(createdTime) ? null : createdTime;
};

const compareLaunchablesByNewest = (
  a: LaunchableFromList,
  b: LaunchableFromList,
) => {
  const aCreatedTime = getLaunchableCreatedTime(a);
  const bCreatedTime = getLaunchableCreatedTime(b);

  if (aCreatedTime !== null || bCreatedTime !== null) {
    return (bCreatedTime ?? 0) - (aCreatedTime ?? 0);
  }

  if (a.id === b.id) return 0;
  return b.id > a.id ? 1 : -1;
};

const LaunchableList: React.FC = () => {
  console.log("Route: /org/:orgId/launchables");
  const history = useHistory();
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const launchableContext = useContext(LaunchableContext);
  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState<string>("");
  const [tab, setTab] = useState<"All" | "Mine" | "Team">("All");
  const [currentPage, setCurrentPage] = useState(1);

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingLaunchable, setPublishingLaunchable] = useState<{
    id: string;
    name: string;
    description: string;
    readmeLink: string;
    launchableData: LaunchableFromList;
  } | null>(null);

  const handlePublishClick = useCallback(
    (
      launchableId: string,
      launchableName: string,
      description: string,
      readmeLink: string,
      launchableData: LaunchableFromList,
    ) => {
      setPublishingLaunchable({
        id: launchableId,
        name: launchableName,
        description,
        readmeLink,
        launchableData,
      });
      setShowPublishModal(true);
    },
    [],
  );

  const handlePublishSuccess = useCallback(async () => {
    // Refresh launchables list from context (not React Query based)
    await launchableContext.refreshActiveLaunchables();
    // Also invalidate queries for other components that use React Query
    queryClient.invalidateQueries({ queryKey: ["launchables"] });
    queryClient.invalidateQueries({ queryKey: ["marketplace-stats"] });
    queryClient.invalidateQueries({ queryKey: ["launchable-details"] });
  }, [queryClient, launchableContext]);

  const displayLaunchables = useMemo(() => {
    // Create a Map to deduplicate by ID
    const uniqueLaunchables = new Map(
      launchableContext.activelaunchables.map((launchable) => [
        launchable.id,
        launchable,
      ]),
    );

    return Array.from(uniqueLaunchables.values())
      .filter((launchable) => {
        if (launchable.rawURL === "") return true;
        try {
          new URL(launchable.rawURL || "");
          return true;
        } catch (e) {
          console.log(e);
          return false;
        }
      })
      .filter((launchable) => {
        if (searchValue === "") return true;
        return launchable.name
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      })
      .filter((launchable) => {
        if (tab === "All") return true;
        if (tab === "Mine")
          return launchable.createdByUserId === userContext.me?.id;
        if (tab === "Team")
          return launchable.createdByUserId !== userContext.me?.id;
        return true;
      })
      .sort(compareLaunchablesByNewest);
  }, [
    launchableContext.activelaunchables,
    searchValue,
    tab,
    userContext.me?.id,
  ]);

  // Pagination
  const totalLaunchablesCount = displayLaunchables.length;
  const itemsPerPage = 8;
  const totalPages = Math.ceil(totalLaunchablesCount / itemsPerPage);
  // Calculate items to display on current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const launchablesToDisplay = displayLaunchables.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  if (orgContext.activeOrg) {
    return (
      <div>
        {/* <InitialTemplateModal /> */}
        <div>
          {/* {workspaces.length === 0 && <Onboardings />} */}
          {launchableContext.loadingActiveLaunchables ? (
            <div className="flex justify-center items-center h-full mt-20">
              <div className="flex flex-col items-center">
                <Spinner />
                <p className="text-white mt-2 font-mono">
                  Loading launchables...
                </p>
              </div>
            </div>
          ) : launchableContext.activelaunchables.length === 0 ? (
            <div className="flex mb-3 mt-4">
              <LaunchableListHeader />
            </div>
          ) : (
            <>
              <div className="flex flex-col mt-6">
                {launchableContext.activelaunchables.length === 0 ? (
                  <>
                    <div
                      className="flex flex-col relative cursor-pointer mt-8"
                      onClick={(e) => {
                        history.push("/launchables/create");
                        e.stopPropagation();
                      }}
                    >
                      <div className="flex w-full justify-center justify-center">
                        <div className="flex flex-row items-center">
                          <span className="text-white font-bold">
                            No Launchables, Create Your First
                          </span>
                          <ArrowUpRightIcon className="h-5 w-5 text-white ml-1" />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-row items-end justify-between w-full">
                      <div className="sm:flex-auto">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                          {tab === "All"
                            ? "Organization Launchables"
                            : tab === "Mine"
                              ? "My Launchables"
                              : "Team Launchables"}
                        </h1>
                      </div>
                      <div className="flex flex-row items-end">
                        <Tabs
                          value={tab}
                          onValueChange={(val) => {
                            setTab(val as "All" | "Mine" | "Team");
                          }}
                        >
                          <TabsList variant="solid">
                            <TabsTrigger value="All">All</TabsTrigger>
                            <TabsTrigger value="Mine">Mine</TabsTrigger>
                            <TabsTrigger value="Team">Team</TabsTrigger>
                          </TabsList>
                        </Tabs>
                        <InputField
                          className="min-w-[300px] ml-5"
                          label=""
                          placeholder="Search Launchables"
                          value={searchValue}
                          onChange={(val) => {
                            if (currentPage !== 1) {
                              setCurrentPage(1);
                            }
                            setSearchValue(val);
                          }}
                          errorMessage=""
                        />
                      </div>
                    </div>
                    <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />

                    <div className="flex flex-col">
                      <>
                        {totalLaunchablesCount === 0 ? (
                          <div className="flex flex-col items-center justify-center my-12 text-secondary">
                            <span className="text-lg font-semibold">
                              No launchables found
                            </span>
                            <span className="text-sm mt-3">
                              Try filtering for different launchables
                            </span>
                          </div>
                        ) : (
                          <>
                            {launchablesToDisplay.map((launchable) => (
                              <LaunchableMeta
                                key={launchable.id}
                                id={launchable.id}
                                launchable={launchable}
                                deleteLaunchable={
                                  launchableContext.deleteLaunchable
                                }
                                onPublishClick={handlePublishClick}
                              />
                            ))}
                          </>
                        )}
                      </>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Single PublishLaunchableModal for all rows */}
        {publishingLaunchable && (
          <PublishLaunchableModal
            isOpen={showPublishModal}
            onClose={() => {
              setShowPublishModal(false);
              setPublishingLaunchable(null);
            }}
            launchableId={publishingLaunchable.id}
            launchableName={publishingLaunchable.name}
            description={publishingLaunchable.description}
            readmeLink={publishingLaunchable.readmeLink}
            launchableData={publishingLaunchable.launchableData}
            orgId={orgContext.activeOrgId}
            onSuccess={handlePublishSuccess}
          />
        )}
      </div>
    );
  }
  return <div />;
};

export default LaunchableList;

export const FreeCreditsButton: React.FC = () => {
  const history = useHistory();
  const orgContext = useContext(OrgContext);
  const billingContext = useContext(BillingContext);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButton(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return billingContext.launchablePoints >= 5 && showButton ? (
    <ShimmerButton>
      <div
        className="flex h-full items-end cursor-pointer shimmer"
        onClick={() => {
          history.push(`/org/${orgContext.activeOrgId}/launchables/redeem`);
        }}
      >
        <div className="flex flex-row text-sm">
          <BanknotesIcon className="h-6 w-6 mr-1 text-white" />
          <h1 className="text-white font-semibold">You Earned Free Credits!</h1>
        </div>
      </div>
    </ShimmerButton>
  ) : null;
};
